import React from 'react'
import { RouteSuggestion, Work } from '@/javascript/domain/entities'
import { RouteItem } from '@/javascript/domain/entities/maps/route-item'
import { RouteMember } from '@/javascript/domain/entities/maps/route-member'
import { palette } from '@/javascript/presentation/helpers/palette'
import RouteSummaryItem, { RouteSummaryData } from './route-summary'

type Props = {
  suggestions: RouteSuggestion[]
  memberNameById: Map<string, string>
  workById: Map<string, Work>
  expandedIndex: number | null
  onToggle: (index: number, isExpanded: boolean) => void
}

const parseMemberName = (name: string): string => {
  const match = name.match(/^(.*?)\s*\(([^)]+)\)$/)
  if (match) return match[1].trim() || match[2].trim()
  return name
}

const toData = (s: RouteSuggestion, memberName: string, workById: Map<string, Work>): RouteSummaryData => ({
  memberName: parseMemberName(memberName),
  pointsCount: s.pointsCount,
  totalTime: s.totalTime,
  serviceTime: s.serviceTime,
  travelTime: s.travelTime,
  totalDistance: s.totalDistance,
  points: s.points.map((p, i, arr) => ({
    isEdge: i === 0 || i === arr.length - 1,
    label: i === 0 || i === arr.length - 1 ? p.location.address : workById.get(p.reference)?.title,
    arrivalTime: p.arrivalTime,
  }))
})

const byMemberName = (memberNameById: Map<string, string>) => (a: RouteSuggestion, b: RouteSuggestion) =>
  (memberNameById.get(a.reference) ?? '').localeCompare(memberNameById.get(b.reference) ?? '', undefined, { numeric: true })

const RouteSummaryList: React.FC<Props> = ({ suggestions, memberNameById, workById, expandedIndex, onToggle }) => (
  <>
    {[...suggestions].sort(byMemberName(memberNameById)).map((suggestion, index) => (
      <RouteSummaryItem
        key={suggestion.reference}
        index={index}
        data={toData(suggestion, memberNameById.get(suggestion.reference) ?? '', workById)}
        color={palette[index % palette.length].color}
        textColor={palette[index % palette.length].textColor}
        expanded={expandedIndex === index}
        onToggle={onToggle}
      />
    ))}
  </>
)

const memberToData = (member: RouteMember): RouteSummaryData => ({
  memberName: parseMemberName(member.accountMemberCompleteData),
  pointsCount: member.pointsCount - 2,
  totalTime: member.totalTimeSeconds,
  serviceTime: member.serviceTimeSeconds,
  travelTime: member.travelTimeSeconds,
  totalDistance: member.totalDistanceMeters,
  points: member.points.map((p, i, arr) => ({
    isEdge: i === 0 || i === arr.length - 1,
    label: i === 0 || i === arr.length - 1 ? p.location.address : p.referenceName,
    arrivalTime: p.arrivalTime,
  }))
})

type RouteItemProps = {
  routes: RouteItem[]
  expandedIndex: number | null
  onToggle: (index: number, isExpanded: boolean) => void
}

export const RouteItemSummaryList: React.FC<RouteItemProps> = ({ routes, expandedIndex, onToggle }) => (
  <>
    {routes.flatMap(r => r.members)
      .sort((a, b) => a.accountMemberCompleteData.localeCompare(b.accountMemberCompleteData, undefined, { numeric: true }))
      .map((member, index) => (
        <RouteSummaryItem
          key={member.id}
          index={index}
          data={memberToData(member)}
          color={palette[index % palette.length].color}
          textColor={palette[index % palette.length].textColor}
          expanded={expandedIndex === index}
          onToggle={onToggle}
        />
      ))}
  </>
)

export default RouteSummaryList
